import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Send, RotateCcw } from "lucide-react";
import { useLang } from "@/i18n";
import { SERVICES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Configuratore() {
    const { t, lang } = useLang();
    const c = t.configurator;
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);
    const [data, setData] = useState({
        service: "", building: "", size: "", timing: "",
        name: "", email: "", phone: "", notes: "",
    });

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    // Prefill from Simulatore (query params: service slug, building key, note)
    const location = useLocation();
    useEffect(() => {
        const q = new URLSearchParams(location.search);
        const svcSlug = q.get("service");
        const bldKey = q.get("building");
        const note = q.get("note");
        const patch = {};
        let startStep = 0;
        if (svcSlug) {
            const svc = SERVICES.find((sv) => sv.slug === svcSlug);
            if (svc) { patch.service = svc[lang].name; startStep = 1; }
        }
        if (bldKey && c.buildings[bldKey]) {
            patch.building = c.buildings[bldKey];
            if (patch.service) startStep = 2;
        }
        if (note) patch.notes = note;
        if (Object.keys(patch).length) {
            setData((d) => ({ ...d, ...patch }));
            setStep(startStep);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const serviceOpts = SERVICES.map((s) => ({ value: s[lang].name, label: s[lang].name }));
    const steps = [
        { key: "service", q: c.q1, opts: serviceOpts },
        { key: "building", q: c.q2, opts: Object.entries(c.buildings).map(([value, label]) => ({ value: label, label })) },
        { key: "size", q: c.q3, opts: Object.entries(c.sizes).map(([value, label]) => ({ value: label, label })) },
        { key: "timing", q: c.q4, opts: Object.entries(c.timings).map(([value, label]) => ({ value: label, label })) },
        { key: "contact" },
    ];
    const total = steps.length;
    const cur = steps[step];
    const canNext = cur.key === "contact" ? true : !!data[cur.key];

    const pick = (val) => {
        set(cur.key, val);
        if (step < total - 1) setTimeout(() => setStep((p) => p + 1), 180);
    };

    const submit = async () => {
        if (!data.name || !data.email) { toast.error(c.required); return; }
        setLoading(true);
        const message =
            `${c.summary}:\n` +
            `- ${c.q1} ${data.service}\n` +
            `- ${c.q2} ${data.building}\n` +
            `- ${c.q3} ${data.size}\n` +
            `- ${c.q4} ${data.timing}\n` +
            (data.notes ? `\n${c.notes}: ${data.notes}` : "");
        try {
            await axios.post(`${API}/contact`, {
                name: data.name, email: data.email, phone: data.phone,
                service: data.service, subject: "Configuratore preventivo",
                message, kind: "quote",
            });
            setDone(true);
        } catch (e) {
            toast.error(c.error);
        } finally {
            setLoading(false);
        }
    };

    const restart = () => {
        setData({ service: "", building: "", size: "", timing: "", name: "", email: "", phone: "", notes: "" });
        setStep(0); setDone(false);
    };

    if (done) {
        return (
            <div data-testid="config-success" className="text-center py-16">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-cb-accent/10 text-cb-accent mb-6">
                    <Check size={40} />
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold text-cb-dark">{c.success}</h3>
                <button onClick={restart} data-testid="config-restart" className="mt-8 inline-flex items-center gap-2 border border-slate-300 px-6 py-3 text-cb-dark hover:border-cb-accent hover:text-cb-accent transition-colors">
                    <RotateCcw size={16} /> {c.restart}
                </button>
            </div>
        );
    }

    const inputCls = "w-full border border-slate-200 bg-white px-4 py-3.5 text-sm outline-none focus:border-cb-accent focus:ring-2 focus:ring-cb-accent/30 transition-colors";

    return (
        <div data-testid="configuratore">
            {/* Progress */}
            <div className="mb-10">
                <div className="flex justify-between text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">
                    <span>{c.step} {step + 1} {c.of} {total}</span>
                    <span>{Math.round(((step + 1) / total) * 100)}%</span>
                </div>
                <div className="h-1 bg-slate-200 overflow-hidden">
                    <motion.div className="h-full bg-cb-accent" animate={{ width: `${((step + 1) / total) * 100}%` }} transition={{ duration: 0.4 }} />
                </div>
            </div>

            <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} transition={{ duration: 0.35 }}>
                    {cur.key !== "contact" ? (
                        <>
                            <h3 className="font-display text-2xl md:text-3xl font-semibold text-cb-dark mb-8">{cur.q}</h3>
                            <div className="grid sm:grid-cols-2 gap-3">
                                {cur.opts.map((o) => (
                                    <button
                                        key={o.value}
                                        data-testid={`config-opt-${cur.key}-${o.value}`}
                                        onClick={() => pick(o.value)}
                                        className={`group flex items-center justify-between border p-5 text-left transition-colors ${
                                            data[cur.key] === o.value ? "border-cb-accent bg-cb-accent/5 text-cb-dark" : "border-slate-200 text-slate-700 hover:border-cb-accent"
                                        }`}
                                    >
                                        <span className="font-medium">{o.label}</span>
                                        <span className={`w-5 h-5 rounded-full border flex items-center justify-center ${data[cur.key] === o.value ? "border-cb-accent bg-cb-accent text-white" : "border-slate-300"}`}>
                                            {data[cur.key] === o.value && <Check size={12} />}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </>
                    ) : (
                        <>
                            <h3 className="font-display text-2xl md:text-3xl font-semibold text-cb-dark mb-2">{c.q5}</h3>
                            <div className="text-sm text-slate-500 mb-6 space-y-1">
                                <div><span className="text-slate-400">{c.q1}</span> {data.service}</div>
                                <div><span className="text-slate-400">{c.q2}</span> {data.building} · {data.size} · {data.timing}</div>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <input data-testid="config-name" className={inputCls} placeholder={c.name + " *"} value={data.name} onChange={(e) => set("name", e.target.value)} />
                                <input data-testid="config-email" type="email" className={inputCls} placeholder={c.email + " *"} value={data.email} onChange={(e) => set("email", e.target.value)} />
                            </div>
                            <input data-testid="config-phone" className={inputCls + " mt-4"} placeholder={c.phone} value={data.phone} onChange={(e) => set("phone", e.target.value)} />
                            <textarea data-testid="config-notes" rows={3} className={inputCls + " mt-4"} placeholder={c.notes} value={data.notes} onChange={(e) => set("notes", e.target.value)} />
                        </>
                    )}
                </motion.div>
            </AnimatePresence>

            {/* Nav */}
            <div className="flex items-center justify-between mt-10">
                <button
                    data-testid="config-back"
                    onClick={() => setStep((p) => Math.max(0, p - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-2 text-slate-500 hover:text-cb-dark transition-colors disabled:opacity-0"
                >
                    <ArrowLeft size={16} /> {c.back}
                </button>

                {cur.key !== "contact" ? (
                    <button
                        data-testid="config-next"
                        onClick={() => canNext && setStep((p) => p + 1)}
                        disabled={!canNext}
                        className="cb-glow inline-flex items-center gap-2 bg-cb-accent hover:bg-cb-accentHover text-white px-8 py-3.5 font-medium transition-colors disabled:opacity-50"
                    >
                        {c.next} <ArrowRight size={16} />
                    </button>
                ) : (
                    <button
                        data-testid="config-submit"
                        onClick={submit}
                        disabled={loading}
                        className="cb-glow inline-flex items-center gap-2 bg-cb-accent hover:bg-cb-accentHover text-white px-8 py-3.5 font-medium transition-colors disabled:opacity-60"
                    >
                        {loading ? c.sending : c.submit} {!loading && <Send size={16} />}
                    </button>
                )}
            </div>
        </div>
    );
}
